import { OrderSection } from '../order-section/order-section';
import React, { useEffect, useState } from 'react';
import { Order } from '../../../clients/quipli-service/order/order';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import './order-invoices.scss';
import { InvoiceList } from '../../collectibles/invoice-list';
import { Card } from '../../../components/card/card';
import { displayMoneyFormat } from '../../../util/util';
import { DateTime } from 'luxon';
import _ from 'lodash';
import { PaymentStatus } from '../../../clients/quipli-service/payment/payment';
import { InvoiceStatus } from '../../../clients/quipli-service/invoice/invoice';
import classNames from 'classnames';

export const OrderInvoices = ({
  order,
  refresh,
  className,
}: {
  order: Order;
  refresh: () => Promise<Order>;
  className?: string;
}) => {
  const { getMultipleConfigurations } = useAPI();
  const [currency, setCurrency] = useState<string>();
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>();
  const [taxInclusive, setTaxInclusive] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string>('Tax');

  useEffect(() => {
    getMultipleConfigurations([
      'CONF_CURRENCY',
      'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
      'CONF_SHOW_PRICES_WITH_TAX',
      'CONF_PRICE_TAX_LABEL',
    ]).then((confs) => {
      setCurrency(confs.find((c) => c.variable === 'CONF_CURRENCY')?.value || 'USD');
      setDisplayTaxByJurisdiction(
        confs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1',
      );
      setTaxInclusive(confs.find((config) => config.variable == 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
      setTaxLabel(confs.find((config) => config.variable == 'CONF_PRICE_TAX_LABEL')?.value || 'Tax');
    });
  }, []);

  return (
    <OrderSection className={classNames('order-invoices flex flex-column gap-2', className)} header="Invoices">
      {currency && order && displayTaxByJurisdiction !== undefined && (
        <>
          <div className="summary-row">
            <Card className="flex flex-column grow">
              <span>Past Due</span>
              <span>
                {displayMoneyFormat(
                  _.sum(
                    order.invoices
                      .filter(
                        (inv) =>
                          inv.status === InvoiceStatus.Approved &&
                          inv.dueDate &&
                          inv.dueDate!.diff(DateTime.now()).as('days') < 0,
                      )
                      .map((inv) => inv.balance),
                  ),
                  currency,
                )}
              </span>
            </Card>
            <Card className="flex flex-column grow">
              <span>Payable</span>
              <span>
                {displayMoneyFormat(
                  _.sum(
                    order.invoices
                      .filter(
                        (inv) =>
                          inv.status === InvoiceStatus.Approved &&
                          (!inv.dueDate || inv.dueDate!.diff(DateTime.now()).as('days') >= 0),
                      )
                      .map((inv) => inv.balance),
                  ),
                  currency,
                )}
              </span>
            </Card>
            <Card className="flex flex-column grow">
              <span>Received</span>
              <span>
                {displayMoneyFormat(
                  _.sum(
                    order.invoices
                      .flatMap((inv) => inv.payments.filter((p) => p.status === PaymentStatus.succeeded))
                      .flatMap((p) => p.invoicePayments.map((_) => _.amount)),
                  ),
                  currency,
                )}
              </span>
            </Card>
          </div>
          <InvoiceList
            invoices={order.invoices}
            authorizedRep={order.authorizedRepresentative}
            currency={currency}
            refresh={() => refresh()}
            enableInvoiceLink={false}
            enabledColumns={['InvoiceNumber', 'DueDate', 'Date', 'Status', 'Total', 'Payments', 'Balance', 'Actions']}
            showCycleBillingActions={order.isCycleBilled}
            displayTaxByJurisdiction={displayTaxByJurisdiction}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
          />
        </>
      )}
    </OrderSection>
  );
};
